import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Plus, Pencil, Eye, Trash2, ExternalLink, BadgeIndianRupee } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDate } from "../../lib/api";
import { PageHeader, StatusBadge, LoadingRows, EmptyState } from "../../components/Shared";
import { MonChip } from "./CreatorMonetization";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const detectLocal = (u) => {
  if (/(?:youtube\.com\/(?:watch|shorts)|youtu\.be\/)/.test(u)) return "YouTube";
  if (/instagram\.com\/(reels?|p)\//.test(u)) return "Instagram";
  if (/facebook\.com\/(watch|[^/]+\/videos|reel)|fb\.watch\//.test(u)) return "Facebook";
  return "";
};

const EMPTY = { url: "", title: "", description: "", category: "", tags: "", language: "Telugu", offer_id: "", status: "published" };

export default function CreatorVideos({ autoOpen = false }) {
  const [videos, setVideos] = useState(null);
  const [offers, setOffers] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [busyMon, setBusyMon] = useState(null);
  const navigate = useNavigate();

  const monetize = async (v) => {
    setBusyMon(v.id);
    try { await api.post(`/vid/creator/videos/${v.id}/monetize`); toast.success("Monetization requested — under review"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusyMon(null); }
  };

  const load = () => {
    api.get("/vid/creator/videos").then((r) => setVideos(r.data.videos)).catch((e) => { toast.error(errMsg(e)); setVideos([]); });
    api.get("/vid/offers").then((r) => setOffers(r.data.offers)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const detected = detectLocal(form.url);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDlg(true); };
  useEffect(() => { if (autoOpen) openNew(); }, []);
  const openEdit = (v) => {
    setEditing(v);
    setForm({ url: v.source_url, title: v.title, description: v.description, category: v.category,
              tags: (v.tags || []).join(", "), language: v.language, offer_id: v.offer_id || "none", status: v.status === "draft" ? "draft" : "published" });
    setDlg(true);
  };

  const save = async () => {
    if (!form.url.trim()) { toast.error("Video URL is required"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
                      offer_id: form.offer_id === "none" ? "" : form.offer_id };
    try {
      if (editing) {
        await api.patch(`/vid/creator/videos/${editing.id}`, payload);
        toast.success("Video updated");
      } else {
        const { data } = await api.post("/vid/creator/videos", payload);
        toast.success(`Video ${data.video.status === "published" ? "published" : "submitted for review"}!`);
      }
      setDlg(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const del = async (v) => {
    try { await api.delete(`/vid/creator/videos/${v.id}`); toast.success("Video removed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggleStatus = async (v) => {
    const next = v.status === "published" ? "draft" : "published";
    try { await api.patch(`/vid/creator/videos/${v.id}`, { status: next }); toast.success(next === "published" ? "Published" : "Moved to draft"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="creator-videos-page">
      <PageHeader title="My Videos" subtitle="Link-based videos — originals stay on YouTube/Instagram/Facebook" testid="creator-videos-title">
        <Button data-testid="add-video-btn" onClick={openNew} className="rounded-full font-bold gradient-bg text-white border-0 glow-sm"><Plus className="h-4 w-4 mr-1.5" />Add Video</Button>
      </PageHeader>

      {!videos ? <LoadingRows rows={4} /> : videos.length === 0 ? (
        <EmptyState title="No videos yet" body="Paste a YouTube, Instagram Reel or Facebook video link — MILTROO creates a public, SEO-friendly watch page.">
          <Button data-testid="add-video-empty" onClick={openNew} className="rounded-full font-bold gradient-bg text-white border-0"><Plus className="h-4 w-4 mr-1" />Add Video</Button>
        </EmptyState>
      ) : (
        <div className="space-y-3">
          {videos.map((v) => (
            <div key={v.id} data-testid={`creator-video-${v.id}`} className="bg-card border border-border rounded-xl p-4 flex flex-wrap items-center gap-4">
              <div className="h-16 w-28 rounded-lg overflow-hidden shrink-0 relative">
                {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full" style={{ background: "linear-gradient(135deg,#007A4D,#2563EB)" }} />}
                <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[8px] font-extrabold uppercase px-1.5 py-0.5 rounded">{v.platform_label}</span>
              </div>
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-display font-bold text-sm">{v.title}</p>
                  <StatusBadge status={v.status} />
                  <MonChip status={v.monetization?.status || "off"} reason={v.monetization?.reason} />
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{v.category || "—"} · {v.views} views · {v.likes} likes · {fmtDate(v.created_at)}{v.offer_name ? ` · 🔗 ${v.offer_name}` : ""}</p>
                {(v.monetization?.status === "active") && (
                  <p className="text-[11px] font-bold text-emerald-600 mt-0.5" data-testid={`video-mon-stats-${v.id}`}>
                    {v.monetization.eligible_views} eligible · {v.monetization.invalid_views} invalid · ₹{(v.monetization.earn_pending + v.monetization.earn_approved + v.monetization.earn_paid).toFixed(2)} earned
                  </p>)}
              </div>
              <div className="flex gap-2 flex-wrap">
                {v.status === "published" && (v.monetization?.status || "off") === "off" && (
                  <Button size="sm" variant="outline" data-testid={`video-monetize-${v.id}`} disabled={busyMon === v.id} onClick={() => monetize(v)}
                    className="rounded-full font-bold text-emerald-600 border-emerald-500/40">
                    {busyMon === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><BadgeIndianRupee className="h-3.5 w-3.5 mr-1" />Monetize</>}</Button>)}
                {v.status === "published" && (
                  <Button size="sm" variant="ghost" data-testid={`video-view-${v.id}`} onClick={() => navigate(`/v/${v.slug}`)} className="rounded-full font-bold"><Eye className="h-3.5 w-3.5 mr-1" />View</Button>)}
                {["published", "draft"].includes(v.status) && (
                  <Button size="sm" variant="outline" data-testid={`video-toggle-${v.id}`} onClick={() => toggleStatus(v)} className="rounded-full font-bold">
                    {v.status === "published" ? "Unpublish" : "Publish"}</Button>)}
                <Button size="sm" variant="outline" data-testid={`video-edit-${v.id}`} onClick={() => openEdit(v)} className="rounded-full font-bold"><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
                <Button size="sm" variant="ghost" data-testid={`video-del-${v.id}`} onClick={() => del(v)} className="rounded-full font-bold text-red-500"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="video-dialog" className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Video" : "Add Video"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Video URL</Label>
              <Input data-testid="video-url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="Paste YouTube, Instagram Reel, or Facebook Video URL" />
              {detected && <p className="text-xs font-bold text-emerald-600" data-testid="video-detected">✓ {detected} detected automatically</p>}
              {form.url && !detected && <p className="text-xs font-bold text-red-500" data-testid="video-invalid">Unsupported URL — use YouTube, Instagram or Facebook</p>}
            </div>
            <div className="space-y-1.5"><Label>Title *</Label><Input data-testid="video-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Low Cost Website Designing in Telugu | ₹5,000 Website" /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="video-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Category</Label><Input data-testid="video-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Website Design" /></div>
              <div className="space-y-1.5"><Label>Language</Label><Input data-testid="video-language" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Tags (comma separated)</Label><Input data-testid="video-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="website design, telugu website, low cost" /></div>
            <div className="space-y-1.5">
              <Label>Affiliate Offer (optional)</Label>
              <Select value={form.offer_id || "none"} onValueChange={(v) => setForm({ ...form, offer_id: v })}>
                <SelectTrigger data-testid="video-offer"><SelectValue placeholder="No affiliate offer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No affiliate offer</SelectItem>
                  {offers.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} — {o.commission_type === "pct" ? `${o.commission_value}%` : `₹${o.commission_value}`} commission</SelectItem>)}
                </SelectContent>
              </Select>
              {offers.length === 0 && <p className="text-[11px] text-muted-foreground">No active offers yet — check <Link to="/creator/offers" className="font-bold underline">Affiliate Products & Services</Link></p>}
            </div>
            <Button data-testid="video-save" onClick={save} disabled={busy || (form.url && !detected)} className="w-full h-12 rounded-full font-bold gradient-bg text-white border-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save Changes" : "Publish Video"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
